import { useId } from "react";
import { Link } from "react-router";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { EASE, VIEW } from "../bits";
import { EVENT } from "./event";
import "./Patch.css";

/* Variant B: the event as an embroidered Halloween patch (satin-stitch border,
   arched lettering, moon and bat, MOVIE NIGHT ribbon), with a hang tag tied to
   it by a thread that carries the details and the poster clipped on. */

const C = 150; // patch center in its 300 × 300 viewBox
const arc = (r: number) => `M ${C - r},${C} A ${r},${r} 0 0 1 ${C + r},${C}`;
const MOON = "M6 -16 A17 17 0 1 0 6 16 A11 11 0 1 1 6 -16 Z";
const BAT = "M20 6 c1-3 2-4 2-4 l1 3 c1-.5 2-.5 2 0 c4-2 9-3 15-1 c-3 1-5 3-5 6 c-2-2-4-2-6 0 c-1-2-3-2-5 1 c-1 2-3 4-4 6 c-1-2-3-4-4-6 c-2-3-4-3-5-1 c-2-2-4-2-6 0 c0-3-2-5-5-6 c6-2 11-1 15 1 c0-.5 1-.5 2 0 l1-3 c0 0 1 1 2 4 Z";

export function EventPatch() {
  const id = useId().replace(/:/g, "");
  return <motion.div className="evp" initial="off" whileInView="on" viewport={VIEW}>
    <Link to={EVENT.href} className="evp-link">
      <motion.span className="evp-patch"
        variants={{ off: { opacity: 0, scale: 0.86, rotate: -16 }, on: { opacity: 1, scale: 1, rotate: -6, transition: { duration: 0.7, ease: EASE } } }}>
        <svg viewBox="0 0 300 300" role="img" aria-label={`Embroidered patch: ${EVENT.title}, ${EVENT.kicker}, ${EVENT.month} ${EVENT.day}`}>
          <defs>
            {/* Twill weave for the patch body */}
            <pattern id={`${id}-twill`} width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <rect width="5" height="5" fill="var(--k-card)" />
              <line x1="0" y1="0" x2="0" y2="5" stroke="var(--k-ink)" strokeOpacity=".07" strokeWidth="1.6" />
            </pattern>
            {/* Satin fills: fine angled thread lines over a thread color */}
            <pattern id={`${id}-satin`} width="2.4" height="2.4" patternUnits="userSpaceOnUse" patternTransform="rotate(62)">
              <rect width="2.4" height="2.4" fill="var(--k-strong)" />
              <line x1="0" y1="0" x2="0" y2="2.4" stroke="#fff" strokeOpacity=".2" strokeWidth=".7" />
            </pattern>
            <pattern id={`${id}-satin-red`} width="2.4" height="2.4" patternUnits="userSpaceOnUse" patternTransform="rotate(-58)">
              <rect width="2.4" height="2.4" fill="var(--k-accent)" />
              <line x1="0" y1="0" x2="0" y2="2.4" stroke="#fff" strokeOpacity=".22" strokeWidth=".7" />
            </pattern>
            <pattern id={`${id}-satin-gold`} width="2.2" height="2.2" patternUnits="userSpaceOnUse" patternTransform="rotate(30)">
              <rect width="2.2" height="2.2" fill="var(--k-pop)" />
              <line x1="0" y1="0" x2="0" y2="2.2" stroke="#000" strokeOpacity=".14" strokeWidth=".6" />
            </pattern>
            <filter id={`${id}-raise`} x="-10%" y="-10%" width="120%" height="120%">
              <feDropShadow dx="0" dy="1.1" stdDeviation=".5" floodColor="#000" floodOpacity=".35" />
            </filter>
            <path id={`${id}-top`} d={arc(103)} />
          </defs>

          {/* Merrowed edge + satin border */}
          <circle cx={C} cy={C} r="146" fill="var(--k-pop)" />
          <motion.circle cx={C} cy={C} r="137.5" fill="none" stroke="var(--k-pop)" strokeWidth="19"
            variants={{ off: { pathLength: 0 }, on: { pathLength: 1, transition: { duration: 1.1, ease: "easeInOut", delay: 0.15 } } }} />
          <circle cx={C} cy={C} r="137.5" fill="none" stroke="#000" strokeOpacity=".16" strokeWidth="19" strokeDasharray=".7 2.1" />
          <circle cx={C} cy={C} r="137.5" fill="none" stroke="#fff" strokeOpacity=".32" strokeWidth="15" strokeDasharray=".6 2.2" strokeDashoffset="1.3" />
          <circle cx={C} cy={C} r="146.5" fill="none" stroke="#000" strokeOpacity=".2" strokeWidth="1.2" />

          {/* Twill body with a running stitch just inside the border */}
          <circle cx={C} cy={C} r="128" fill={`url(#${id}-twill)`} />
          <circle cx={C} cy={C} r="122" fill="none" stroke="var(--k-strong)" strokeOpacity=".55" strokeWidth="1.4" strokeDasharray="4 3.2" />

          <g filter={`url(#${id}-raise)`}>
            <text className="evp-arc" fill={`url(#${id}-satin)`}>
              <textPath href={`#${id}-top`} startOffset="50%" textAnchor="middle">STITCHES NIGHT OUT</textPath>
            </text>
            {/* Halloween motifs below the ribbon: a gold satin moon between two bats */}
            <path d={MOON} transform="translate(148 244) scale(1.15) rotate(-18)" fill="var(--k-pop)" />
            <path d={MOON} transform="translate(148 244) scale(1.15) rotate(-18)" fill="none" stroke="color-mix(in srgb, var(--k-pop) 50%, #000)" strokeWidth="1.6" />
            <path d={BAT} transform="translate(86 230) scale(.95) rotate(-10 20 9)" fill="var(--k-ink)" />
            <path d={BAT} transform="translate(178 236) scale(.7) rotate(12 20 9)" fill="var(--k-ink)" />

            <text x={C} y="90" textAnchor="middle" className="evp-month" fill="var(--k-ink)">{EVENT.weekday.toUpperCase()} · {EVENT.month.toUpperCase()}</text>
            <text x={C} y="169" textAnchor="middle" className="evp-day" fill={`url(#${id}-satin-red)`}>{EVENT.day}</text>
          </g>

          {/* Ribbon banner, wider than the patch like a real merit badge */}
          <motion.g filter={`url(#${id}-raise)`} variants={{ off: { scaleX: 0 }, on: { scaleX: 1, transition: { delay: 0.55, duration: 0.5, ease: EASE } } }} style={{ transformOrigin: "150px 196px" }}>
            <path d="M10 184 L32 184 L32 216 L10 216 L21 200 Z" fill="var(--k-strong)" style={{ filter: "brightness(.78)" }} />
            <path d="M290 184 L268 184 L268 216 L290 216 L279 200 Z" fill="var(--k-strong)" style={{ filter: "brightness(.78)" }} />
            <path d="M28 179 L272 179 L272 211 L28 211 Z" fill={`url(#${id}-satin)`} />
            <path d="M32 183.5 L268 183.5 M32 206.5 L268 206.5" stroke="var(--k-strong-ink)" strokeOpacity=".7" strokeWidth="1.1" strokeDasharray="3.2 2.6" />
            <text x={C} y="196" textAnchor="middle" dominantBaseline="middle" className="evp-ribbon" fill="var(--k-strong-ink)">MOVIE NIGHT</text>
          </motion.g>
        </svg>
      </motion.span>

      {/* Thread tying the tag to the patch */}
      <svg className="evp-string evp-string-h" viewBox="0 0 96 40" preserveAspectRatio="none" aria-hidden="true"><path d="M0 22 C 30 6, 56 38, 96 20" /></svg>
      <svg className="evp-string evp-string-v" viewBox="0 0 40 60" preserveAspectRatio="none" aria-hidden="true"><path d="M20 0 C 6 20, 34 36, 20 60" /></svg>

      <motion.span className="evp-tagwrap" variants={{ off: { opacity: 0, x: 24 }, on: { opacity: 1, x: 0, transition: { delay: 0.25, duration: 0.6, ease: EASE } } }}>
        <span className="evp-tag">
          <i className="evp-grommet" aria-hidden="true" />
          <small className="evp-kicker">{EVENT.kicker}</small>
          <b className="evp-title">{EVENT.headline[0]} <i>{EVENT.film}</i> {EVENT.headline[1]}</b>
          <span className="evp-meta">
            <span><em>When</em>{EVENT.weekday}, {EVENT.month} {EVENT.day} · {EVENT.time}</span>
            <span><em>Where</em>{EVENT.venue}</span>
          </span>
          <span className="evp-cta">
            <span className="evp-price">{EVENT.price}</span>
            <span className="evp-go">Get tickets <ArrowRight size={16} aria-hidden="true" /></span>
          </span>
        </span>
        <motion.span className="evp-photo" variants={{ off: { opacity: 0, y: -12, rotate: 0 }, on: { opacity: 1, y: 0, rotate: 7, transition: { delay: 0.75, type: "spring", stiffness: 200, damping: 15 } } }}>
          <img src={EVENT.poster} alt={EVENT.posterAlt} />
          <i className="evp-clip" aria-hidden="true" />
        </motion.span>
      </motion.span>
    </Link>
  </motion.div>;
}
